import { useTenancy } from "@/contexts/TenancyContext";
import { db } from "@/js/external-js/config/main_config";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useMSGReadOnly } from "../contexts/ReadOnlyContext";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { useMarkdownComponents } from "./MDXFields";
import { LiveMarkdownEditor } from "./LiveMarkdownEditor";
import { Pencil } from "lucide-react";
import { useMSGVersion } from "../hooks/useMSGVersion";

interface AdditionalContentProps {
    pageRoute: string;
    moduleList?: { id: string; visible: boolean }[];
    clauseStandards?: React.MutableRefObject<Set<string>>;
    pageClauses?: { current: Set<string> };
    updatePageClauses?: (newClauses: Set<string>) => void;
}

const AdditionalContent = ({
    pageRoute,
    moduleList,
    clauseStandards,
    pageClauses,
    updatePageClauses
}: AdditionalContentProps) => {
    const { activeTenancy } = useTenancy();
    const { isReadOnly } = useMSGReadOnly();
    const [dynamicContent, setDynamicContent] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const { incrementVersion } = useMSGVersion();
    
    // Get MDX components for rendering
    const mdxComponents = useMarkdownComponents({
        moduleList: moduleList || [],
        clauseStandards: clauseStandards || { current: new Set<string>() },
        pageClauses: pageClauses || { current: new Set<string>() },
        updatePageClauses: updatePageClauses || (() => {}),
        isReadOnly
    });

    useEffect(() => {
        if (!activeTenancy || !activeTenancy.id || ["", "Loading..."].includes(activeTenancy.id)) return;
        const editorDocRef = db.collection('customers')
            .doc(activeTenancy.id)
            .collection('product')
            .doc('managementSystemGuide')
            .collection('editor')
            .doc('content');
            
        const unsubscribe = editorDocRef.onSnapshot((doc) => {
            if (doc.exists) {
                const data = doc.data();
                if (data && data[pageRoute] && data[pageRoute].content) {
                    setDynamicContent(data[pageRoute].content);
                }
            }
        });
        
        // Cleanup function to unsubscribe when component unmounts
        return () => {
            if (unsubscribe && typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [activeTenancy.id, pageRoute]);
    
    useEffect(() => {
        if (!activeTenancy || !activeTenancy.id || ["", "Loading..."].includes(activeTenancy.id)) return;
        const load = async () => {
            const editorDocRef = db.collection('customers')
                .doc(activeTenancy.id)
                .collection('product')
                .doc('managementSystemGuide')
                .collection('editor')
                .doc('content');
            const doc = await editorDocRef.get();
            if (doc.exists) {
                const data = doc.data();
                if (data && data[pageRoute] && data[pageRoute].content) {
                    setDynamicContent(data[pageRoute].content);
                }
            }
        };
        load();
    }, [pageRoute, activeTenancy.id, activeTenancy]);

    const handleSave = async (newValue: string) => {
        const oldValue = dynamicContent;
        setDynamicContent(newValue);
        const editorDocRef = db.collection('customers')
            .doc(activeTenancy.id)
            .collection('product')
            .doc('managementSystemGuide')
            .collection('editor')
            .doc('content');
        // Use the dynamic pageRoute as the key
        editorDocRef.set({ [pageRoute]: { content: newValue } }, { merge: true })
            .then(async () => {
                // Increment MSG version after successful save with snapshot data
                try {
                    await incrementVersion(
                        'additional_content_update',
                        `Updated additional content on ${pageRoute}`,
                        {
                            pageRoute,
                            oldContent: oldValue,
                            newContent: newValue
                        }
                    );
                    toast.success('Additional content saved successfully');
                } catch (versionError) {
                    console.error('Error updating version:', versionError);
                    toast.success('Content saved, but version update failed');
                }
            })
            .catch(error => {
                console.error('Error saving additional content:', error);
                toast.error('Failed to save additional content');
            });
    };

    if (isReadOnly) {
        // Read-only version - just show rendered MDX
        return (
            <div className="py-4 mb-2">
                <div className="text-sm font-medium mb-1">Additional Content</div>
                {dynamicContent ? (
                    <MarkdownRenderer source={dynamicContent} components={mdxComponents} noPadding />
                ) : (
                    <div className="text-muted-foreground text-sm">No additional content</div>
                )}
            </div>
        );
    }

    if (isEditing) {
        // Editing mode - show live markdown editor with preview
        // Break out of the 56rem max-width constraint and parent padding on large screens
        return (
            <div className="py-4 mb-2 lg:-mx-[10.5rem] lg:max-w-none">
                <div className="px-6">
                    <div className="text-sm font-medium mb-4">Additional Content</div>
                    <LiveMarkdownEditor
                        value={dynamicContent}
                        onSave={(newValue) => {
                            handleSave(newValue);
                            setIsEditing(false);
                        }}
                        onCancel={() => setIsEditing(false)}
                        components={mdxComponents}
                    />
                </div>
            </div>
        );
    }

    // Display mode - show rendered MDX with edit icon
    return (
        <div className="py-4 mb-2 group relative">
            <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium">Additional Content</div>
                <button
                    onClick={() => setIsEditing(true)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-primary/10 rounded"
                    title="Edit content"
                >
                    <Pencil className="h-4 w-4 text-muted-foreground hover:text-primary" />
                </button>
            </div>
            {dynamicContent ? (
                <div onClick={() => setIsEditing(true)} className="cursor-pointer">
                    <MarkdownRenderer source={dynamicContent} components={mdxComponents} noPadding />
                </div>
            ) : (
                <div 
                    onClick={() => setIsEditing(true)}
                    className="text-muted-foreground text-sm italic cursor-pointer hover:text-primary"
                >
                    Click to add content...
                </div>
            )}
        </div>
    )
}

export default AdditionalContent;
export { AdditionalContent };